import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Grid,
    Box,
    Divider,
    IconButton,
    Stack,
    Link
} from '@mui/material';
import { X, User, Mail, MapPin, Briefcase, FileText, Calendar, DollarSign, MessageSquare } from 'lucide-react';

const ApplicationDetailsModal = ({ open, onClose, application }) => {
    if (!application) return null;

    const DetailItem = ({ icon: Icon, label, value, fullWidth = false }) => (
        <Grid size={{ xs: 12, md: fullWidth ? 12 : 6 }}>
            <Box sx={{ mb: 3 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                    <Icon size={14} style={{ color: '#FFBD65' }} />
                    <Typography
                        variant="caption"
                        sx={{
                            color: '#999',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            fontSize: '0.7rem'
                        }}
                    >
                        {label}
                    </Typography>
                </Stack>
                <Typography
                    variant="body1"
                    sx={{
                        color: '#222',
                        fontWeight: 400,
                        lineHeight: 1.3,
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-line'
                    }}
                >
                    {value || '—'}
                </Typography>
            </Box>
        </Grid>
    );

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'pending': return { bg: '#FFF3E0', color: '#EF6C00' };
            case 'reviewing': return { bg: '#E3F2FD', color: '#1976D2' };
            case 'shortlisted': return { bg: '#E8F5E9', color: '#2E7D32' };
            case 'rejected': return { bg: '#FFEBEE', color: '#C62828' };
            default: return { bg: '#F5F5F5', color: '#666' };
        }
    };

    const statusStyle = getStatusColor(application.status);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '6px' } }}>
            <DialogTitle sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Typography sx={{ fontWeight: 600, fontSize: '1.1rem', color: '#222' }}>Application Details</Typography>
                    <Box
                        sx={{
                            display: 'inline-block',
                            px: 1.5, py: 0.3,
                            borderRadius: '4px',
                            bgcolor: statusStyle.bg,
                            color: statusStyle.color,
                            fontWeight: 600,
                            fontSize: '0.7rem'
                        }}
                    >
                        {application.status || 'Pending'}
                    </Box>
                </Stack>
                <IconButton onClick={onClose} size="small">
                    <X size={18} />
                </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{
                p: 3,
                maxHeight: '70vh',
                overflowY: 'auto',
                '&::-webkit-scrollbar': { width: '5px' },
                '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '10px' },
                '&::-webkit-scrollbar-thumb': { background: '#FFBD65', borderRadius: '10px' },
                '&::-webkit-scrollbar-thumb:hover': { background: '#e6a550' }
            }}>
                <Grid container spacing={1}>
                    {/* Personal & Job Info */}
                    <DetailItem icon={User} label="Full Name" value={application.fullName || application.name} />
                    <DetailItem icon={Mail} label="Email Address" value={application.email} />
                    <DetailItem icon={Briefcase} label="Job Title" value={application.job?.title || application.job_title || 'General Application'} />
                    <DetailItem icon={Calendar} label="Applied Date" value={new Date(application.createdAt).toLocaleDateString()} />

                    {/* Locations */}
                    <DetailItem icon={MapPin} label="Current Location" value={application.currentLocation} />
                    <DetailItem icon={MapPin} label="Home Based Location" value={application.homeBasedLocation} />

                    <Grid item xs={12} sx={{ my: 1 }}><Divider sx={{ borderStyle: 'dashed' }} /></Grid>

                    {/* Detailed Questions */}
                    <DetailItem
                        icon={MessageSquare}
                        label="Why work with us?"
                        value={application.whyWorkWithUs}
                        fullWidth
                    />
                    <DetailItem
                        icon={FileText}
                        label="Relevant Experience"
                        value={application.relevantExperience}
                        fullWidth
                    />
                    <DetailItem
                        icon={Calendar}
                        label="Work Schedule (Next Year)"
                        value={application.workScheduleNextYear}
                        fullWidth
                    />

                    <Grid item xs={12} sx={{ my: 1 }}><Divider sx={{ borderStyle: 'dashed' }} /></Grid>

                    {/* Financials & Notes */}
                    <DetailItem icon={DollarSign} label="Salary Expectation" value={application.salaryExpectation} />
                    <DetailItem icon={MessageSquare} label="Additional Notes" value={application.additionalNotes} />

                    {/* CV File */}
                    {application.cvFile && (
                        <Grid item xs={12}>
                            <Box sx={{
                                mt: 2,
                                p: 2,
                                bgcolor: '#F9FAFB',
                                border: '1px solid #E8E8E8',
                                borderRadius: '6px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Box sx={{
                                        width: 36,
                                        height: 36,
                                        bgcolor: 'rgba(255, 189, 101, 0.1)',
                                        borderRadius: '6px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#FFBD65'
                                    }}>
                                        <FileText size={18} />
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#333', fontSize: '0.85rem' }}>Curriculum Vitae (CV)</Typography>
                                        <Typography variant="caption" sx={{ color: '#8E8E8E', fontSize: '0.75rem' }}>Click to view or download</Typography>
                                    </Box>
                                </Stack>
                                <Button disableElevation disableFocusRipple disableRipple disableTouchRipple
                                    component={Link}
                                    href={application.cvFile}
                                    target="_blank"
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        borderColor: '#FFBD65',
                                        color: '#FFBD65',
                                        '&:hover': { borderColor: '#e6a550', bgcolor: 'rgba(255, 189, 101, 0.05)' },
                                        textTransform: 'none',
                                        borderRadius: '6px',
                                        fontSize: '0.85rem'
                                    }}
                                >
                                    Open CV
                                </Button>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2, bgcolor: '#F9FAFB' }}>
                <Button
                    onClick={onClose}
                    variant="contained"
                    sx={{
                        bgcolor: '#FFBD65',

                        color: '#fff',
                        textTransform: 'none',
                        borderRadius: '6px',
                        px: 3,
                        fontSize: '0.85rem'
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ApplicationDetailsModal;
